<template>
  <div class="coro-widget">
    <div
      v-if="widgetConfig.metadata.state === WidgetState.SETUP_REQUIRED"
      class="coro-widget__content coro-widget--empty"
      data-testid="device-widget-no-devices-container"
    >
      <v-icon class="empty-data-icon" :icon="`$${CoroIcons.ENDPOINT_DATA_GOVERNANCE_S}`" />
      <div class="subtitle1 text-center">
        {{ $t(`dashboard.widgets.${widgetConfig.type}.noDataLossProtection`) }}
      </div>
      <div class="caption text-indigo-medium mb-4">
        {{ $t(`dashboard.widgets.${widgetConfig.type}.noDLPServicesConnected`) }}
      </div>
      <v-btn
        v-if="canManageDevicesSettings"
        :to="{ name: RouteName.DEVICES_SETTINGS_PAGE }"
        color="primary"
        rounded
        data-testid="device-widget-add-protection-btn"
      >
        {{ $t(`dashboard.widgets.${widgetConfig.type}.addProtectionBtn`) }}
      </v-btn>
    </div>

    <div v-else-if="widgetDataValue.data" class="coro-widget__content">
      <div class="coro-widget__tile coro-widget__tile-content">
        <div class="d-inline-flex">
          <div class="d-flex align-start flex-column">
            <div
              class="coro-widget-number"
              data-testid="endpoint-data-governance-widget-protected-devices"
            >
              {{ widgetDataValue.data.protectedDevicesCount }}
            </div>
            <div class="body2">
              <span>{{ $t("general.devices") }}</span>
              <span
                v-if="!isAccessRestricted(RolePermissionsScope.VIEWS, 'devicesView')"
                @click="goToDevices()"
              >
                <a
                  class="coro-link text-no-wrap ml-2"
                  data-testid="endpoint-data-governance-widget-view-devices"
                >
                  {{ $t("general.view") }} ›
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="coro-widget__tile">
        <vulnerabilities-tile
          :widget-data="widgetDataValue.data"
          @all-active-link-clicked="viewAllTickets(true, widgetFilter)"
          @all-caught-up-link-clicked="viewAllTickets(false, widgetFilter)"
          @vulnerability-clicked="viewVulnerability($event, widgetFilter)"
        ></vulnerabilities-tile>
        <generated-tickets-trend-chart
          v-if="widgetConfig.metadata.state === WidgetState.HAS_OPEN_TICKETS"
          class="pt-2 px-6"
          :chart-data="widgetDataValue.data.generatedTicketsHistory"
          :delta="widgetDataValue.data.generatedTicketsDelta"
        ></generated-tickets-trend-chart>
      </div>
    </div>

    <widget-error-state
      v-else
      :widget-type="widgetConfig.type"
      @retry-click="$emit('fetch-data')"
    />
  </div>
</template>

<script lang="ts">
import { TicketsModuleFilter } from "@/constants/tickets";
import { computed, defineComponent, type PropType } from "vue";
import VulnerabilitiesTile from "@/components/VulnerabilitiesTile.vue";
import kebabCase from "lodash/kebabCase";
import { useWidgetContent } from "@/composables/useWidgetContent";
import { RouteName } from "@/constants/routes";
import { type WidgetConfiguration, WidgetState } from "@/_helpers/dashboard.ts";
import { CoroIcons } from "@/constants/coro-icon.ts";
import GeneratedTicketsTrendChart from "@/components/dashboard/GeneratedTicketsTrendChart.vue";
import type { WidgetType } from "@/constants/dashboard.ts";
import WidgetErrorState from "@/components/dashboard/WidgetErrorState.vue";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";
import { useRouter } from "vue-router";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

export default defineComponent({
  props: {
    widgetConfig: {
      type: Object as PropType<WidgetConfiguration<WidgetType.ENDPOINT_DATA_GOVERNANCE>>,
      required: true,
    },
  },
  components: {
    WidgetErrorState,
    GeneratedTicketsTrendChart,
    VulnerabilitiesTile,
  },
  setup(props) {
    const router = useRouter();
    const widgetDataValue = computed(() => props.widgetConfig.data);

    const canManageDevicesSettings = computed(
      () =>
        !isAccessRestricted(
          RolePermissionsScope.WORKSPACE_MANAGEMENT,
          WorkspaceManagementScopeSections.DEVICES
        )
    );

    const { viewVulnerability, viewAllTickets, isAccessRestricted } = useWidgetContent();

    const goToDevices = async () => {
      await router.push({ name: RouteName.DEVICES });
      trackGtmEvent(
        GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
        `${startCase(props.widgetConfig.type)} - ${GTMParams.VIEW_DEVICES}`
      );
    };

    return {
      canManageDevicesSettings,
      widgetDataValue,
      viewVulnerability,
      viewAllTickets,
      isAccessRestricted,
      kebabCase,
      widgetFilter: TicketsModuleFilter.ENDPOINT_DATA_GOVERNANCE,
      RouteName,
      WidgetState,
      CoroIcons,
      RolePermissionsScope,
      goToDevices,
    };
  },
});
</script>
